import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataObjectLink } from '@mmlogic/components';
import { ApiClientService } from 'src/app/core/services/api-client.service';
import { TenantClientService } from 'src/app/core/services/tenant-client.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { AppService } from 'src/app/core/services/app.service';
import { DataObject } from 'src/app/core/model/data-model/data-object.model';
import { AccessVisibility } from 'src/app/core/model/data-model/object-access.model';

/**
 * Technical/admin view of who can reach one record and how (ReBAC audit).
 *
 * Deliberately two independent calls: the ordinary object GET for the record's own facts,
 * and the admin-only /audit/.../access endpoint for the ACL. The audit call can fail (or be
 * slow) without ever hiding the record itself — see docs/relation-based-access-control.md §12
 * in content-api.
 */
@Component({
    selector: 'app-object-access',
    templateUrl: './object-access.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ObjectAccessComponent implements OnInit {
  tenantCode: string;
  type: string;
  id: string;

  dataObject: DataObject | undefined;
  objectLoading = true;
  objectError = false;

  access: AccessVisibility | undefined;
  accessLoading = true;
  accessError = false;

  rebuilding = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiClientService,
    private tenantClient: TenantClientService,
    private notificationService: NotificationService,
    public appService: AppService
  ) {}

  /** Every class the object is an instance of, as presented on the object itself — falls
   *  back to the route's (plural) type when `_classes` is absent. */
  get types(): string[] {
    return this.dataObject?._classes ?? [this.type];
  }

  /** _links entries that point at other objects, as a single named link or (for m-n relations
   *  declared on this side) an array of named links under `.values`. A link with neither is a
   *  to-many relation not registered at the object level and isn't a relation we can show here.
   *  Platform links (self, metadata, relatedDataObject, form, scripts, …) are exactly the keys
   *  starting with `_`; every other key is a relation name. */
  get relations(): { key: string; link: DataObjectLink }[] {
    if (!this.dataObject) return [];
    return Object.entries(this.dataObject._links)
      .filter(([key]) => !key.startsWith('_'))
      .map(([key, link]) => ({ key, link: link as DataObjectLink }))
      .filter(({ link }) => !!link.name || (link.values?.length ?? 0) > 0);
  }

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    this.tenantCode = params['tenantCode'];
    this.type = params['type'];
    this.id = params['id'];

    this.loadObject();
    this.loadAccess();
  }

  backToObject(): void {
    this.router.navigate(['/data', this.tenantCode, this.type, this.id]);
  }

  /** Remedy offered next to every 'unresolved' row — see AccessVisibility.unresolved. */
  rebuildAccessControl(): void {
    this.rebuilding = true;
    this.tenantClient.rebuildAccessControl(this.tenantCode).subscribe({
      next: () => {
        this.rebuilding = false;
        this.notificationService.show({ message: 'Access control rebuild is gestart.', level: 'INFO' });
        this.loadAccess();
      },
      error: (error) => {
        this.rebuilding = false;
        this.notificationService.showServerError(error, 'accessControl.rebuild.failed', 'WARNING');
      },
    });
  }

  private loadObject(): void {
    this.objectLoading = true;
    this.objectError = false;
    this.api.getObject(this.tenantCode, this.type, this.id).subscribe({
      next: (dataObject) => {
        this.dataObject = dataObject;
        this.objectLoading = false;
      },
      error: () => {
        this.objectError = true;
        this.objectLoading = false;
      },
    });
  }

  private loadAccess(): void {
    this.accessLoading = true;
    this.accessError = false;
    this.api.getAccessVisibility(this.tenantCode, this.type, this.id).subscribe({
      next: (access) => {
        this.access = access;
        this.accessLoading = false;
      },
      error: () => {
        this.accessError = true;
        this.accessLoading = false;
      },
    });
  }
}
